import { buildCheckoutProblems, type FormErrorField } from './buildCheckoutProblems';
import {
  customerValidationPolicyProblem,
  missingCarrierFields,
  type CustomerCheckoutValues,
  type CustomerValidationPolicy,
  type MissingCustomerField,
} from './customerCheckout';
import type { PosItemError } from './validatePosItems';
import type { FulfillmentChoice } from '../components/Fulfillment/fulfillmentChoice';

export interface CheckoutGateInput {
  /** Số dòng hàng trong giỏ. */
  itemCount: number;
  /** Kết quả `validatePosItems` — POSPage đã tính để tô đỏ dòng hàng. */
  itemErrors: Record<string, PosItemError>;
  /** `errorFields` từ `form.validateFields()` (rỗng nếu validate pass). */
  errorFields: FormErrorField[];
  /** Trường khách còn trống, theo `useMissingCustomerFields`. */
  missing: MissingCustomerField[];
  /** Đã gắn khách vào đơn chưa (`customer_phone` có giá trị). */
  hasCustomer: boolean;
  /**
   * Snapshot của form store, PHẢI đọc bằng `getFieldsValue(true)`: cổng ĐVVC đọc
   * `shipping_partner` / `package_weight`, hai field không nằm trong Form.Item
   * nào đang mount ở chế độ nhận tại quầy.
   */
  values: CustomerCheckoutValues;
  customerValidationPolicy: CustomerValidationPolicy;
  /**
   * `pos_default_fulfillment_choice` (Settings → POS) — cổng ĐVVC
   * (`missingCarrierFields`) cần nó để chuẩn hoá `shipping_mode` HIỆU LỰC,
   * giống hệt `buildCreateOrderPayload`. Final review fix C1: thiếu tham số
   * này thì cổng đọc `values.shipping_mode` THÔ, và một đơn giao hàng chưa ai
   * bấm thanh PTNH (thô rỗng) lọt qua cổng dù mặc định tenant là `'carrier'`.
   */
  defaultFulfillmentChoice: FulfillmentChoice;
}

export type CheckoutGateResult =
  | { ok: true }
  | { ok: false; problems: string[] };

/**
 * MỘT quyết định duy nhất cho câu hỏi "có được ghi đơn không, nếu không thì nói
 * gì với thu ngân".
 *
 * Gộp hai cổng trước đây nằm rời trong `POSPage.handleCheckout`: danh sách trường
 * khách/hoá đơn/dòng hàng còn thiếu (`buildCheckoutProblems`) và cổng đơn vị vận
 * chuyển (`missingCarrierFields`). Chúng từng là hai khối `if` với cùng một cụm
 * JSX toast dài 15 dòng dán hai lần — một chỗ sửa nhãn mà quên chỗ kia là hai
 * kiểu thông báo khác nhau cho cùng một hành động.
 *
 * THỨ TỰ CÓ Ý NGHĨA — cổng ĐVVC chỉ được xét khi danh sách đầu đã rỗng, giữ đúng
 * hành vi cũ: đơn còn thiếu thông tin khách thì toast chỉ liệt kê phần khách. Hai
 * nhóm cần hai hành động khác nhau của thu ngân (mở CustomerDialog sửa thông tin
 * vs. chọn hãng ở cột giữa); trộn vào một danh sách là bắt họ làm hai việc cùng
 * lúc và mất luôn tiêu điểm mà `scrollToField` đang nhắm tới.
 *
 * Hàm THUẦN: không đọc form, không bắn toast, không mở dialog. Mọi side effect
 * (mở CustomerDialog, `scrollToField`, `message.error`) ở lại `POSPage` — nhờ vậy
 * toàn bộ luật chặn ở đây test được mà không cần dựng React.
 */
export function runCheckoutGates({
  itemCount,
  itemErrors,
  errorFields,
  missing,
  hasCustomer,
  values,
  customerValidationPolicy,
  defaultFulfillmentChoice,
}: CheckoutGateInput): CheckoutGateResult {
  const policyProblem = customerValidationPolicyProblem(customerValidationPolicy);
  if (policyProblem) return { ok: false, problems: [policyProblem] };

  const problems = buildCheckoutProblems({
    itemCount,
    itemErrors,
    errorFields,
    missing,
    hasCustomer,
  });
  if (problems.length > 0) return { ok: false, problems };

  const missingCarrier = missingCarrierFields(values, defaultFulfillmentChoice);
  if (missingCarrier.length > 0) {
    return { ok: false, problems: missingCarrier.map((m) => m.label) };
  }

  return { ok: true };
}
